import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Anime,
  BroadcastDay,
  BROADCAST_DAYS,
  SEASON_CONFIGS,
  WakuFeedItem,
} from '../types/anime';
import {
  fetchWakuRecent,
  fetchWakuAllAiring,
  searchWakuAnime,
  fetchBilibiliRecent,
  searchBilibiliAnime,
  searchAniOneAnime,
  fetchAniOneRecent,
  fetchRukaAllAiring,
  searchRukaAnime,
} from '../services/animeService';
import { MalScoreBadge } from './MalScoreBadge';
import { sameDataProps } from '../utils/memo';
import {
  formatImageUrl,
  getDayConfig,
  getTodayBroadcastDay,
  areAnimeMatching,
  detectPlatformFromUrl,
  hasConflictingSource,
  normalizeAnimeTitle,
  formatAnimeEpisodeStatus,
} from '../utils/helpers';
import {
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Flame,
  X,
} from 'lucide-react';

interface WakuExplorerViewProps {
  existingAnimeList: Anime[];
  onQuickAddFromWaku: (item: WakuFeedItem) => void;
  onRemoveFromWaku?: (item: WakuFeedItem) => void;
}

type ExplorerPlatform = 'anione' | 'bilibili' | 'anime-waku' | 'animeruka';
type AniOneShelfFilter = 'FALL_2026' | 'THAI_DUB' | 'SUMMER_2026' | 'ALL';

// Per-tab feed cache (memory + sessionStorage) so switching tabs doesn't refetch every time
const FEED_CACHE_FRESH_MS = 10 * 60 * 1000;
const FEED_CACHE_PREFIX = 'explorer_feed_cache:';
const feedMemoryCache = new Map<string, { at: number; items: WakuFeedItem[] }>();

function feedCacheKey(platform: ExplorerPlatform, page: number, mode: 'AIRING_ALL' | 'PAGE') {
  return platform === 'anime-waku' ? `${platform}:${mode}:${mode === 'PAGE' ? page : 0}` : platform;
}

function readFeedCache(key: string) {
  const mem = feedMemoryCache.get(key);
  if (mem) return mem;
  try {
    const raw = sessionStorage.getItem(FEED_CACHE_PREFIX + key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.items)) {
        feedMemoryCache.set(key, parsed);
        return parsed as { at: number; items: WakuFeedItem[] };
      }
    }
  } catch {}
  return null;
}

function writeFeedCache(key: string, items: WakuFeedItem[]) {
  const entry = { at: Date.now(), items };
  feedMemoryCache.set(key, entry);
  try {
    sessionStorage.setItem(FEED_CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {}
}

function fetchFeed(
  platform: ExplorerPlatform,
  page: number,
  mode: 'AIRING_ALL' | 'PAGE',
  forceRefresh = false
): Promise<WakuFeedItem[]> {
  if (platform === 'anione') return fetchAniOneRecent();
  if (platform === 'bilibili') return fetchBilibiliRecent();
  if (platform === 'animeruka') return fetchRukaAllAiring(forceRefresh);
  return mode === 'AIRING_ALL' ? fetchWakuAllAiring(forceRefresh) : fetchWakuRecent(page);
}

export const WakuExplorerView: React.FC<WakuExplorerViewProps> = React.memo(({
  existingAnimeList,
  onQuickAddFromWaku,
  onRemoveFromWaku,
}) => {
  const [activePlatform, setActivePlatform] = useState<ExplorerPlatform>('animeruka');
  const [items, setItems] = useState<WakuFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  // The search term the current `items` came from ('' = platform feed, not search results)
  const [resultsFor, setResultsFor] = useState('');
  const [selectedDay, setSelectedDay] = useState<'ALL' | BroadcastDay>('ALL');
  const [aniOneShelf, setAniOneShelf] = useState<AniOneShelfFilter>('FALL_2026');
  const [wakuMode, setWakuMode] = useState<'AIRING_ALL' | 'PAGE'>('AIRING_ALL');
  const [wakuAudioFilter, setWakuAudioFilter] = useState<'ALL' | 'ซับไทย' | 'พากย์ไทย'>('ALL');

  const todayId = getTodayBroadcastDay();

  // Ignore responses from an older request (e.g. user switched tabs before it finished)
  const requestIdRef = useRef(0);

  const loadRecent = async (
    platform: ExplorerPlatform,
    targetPage: number,
    mode: 'AIRING_ALL' | 'PAGE' = wakuMode,
    forceRefresh = false
  ) => {
    const requestId = ++requestIdRef.current;
    setResultsFor('');
    const key = feedCacheKey(platform, targetPage, mode);
    const cached = forceRefresh ? null : readFeedCache(key);

    // Show cached list instantly; only spin when there is nothing to show yet
    if (cached) {
      setItems(cached.items);
      setIsLoading(false);
      if (Date.now() - cached.at < FEED_CACHE_FRESH_MS) return;
    } else {
      setIsLoading(true);
    }

    try {
      const list = await fetchFeed(platform, targetPage, mode, forceRefresh);
      if (list.length > 0) writeFeedCache(key, list);
      if (requestId === requestIdRef.current && (list.length > 0 || !cached)) {
        setItems(list);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!query.trim()) {
      loadRecent(activePlatform, page, wakuMode);
    }
  }, [activePlatform, page, wakuMode]);

  // Warm up the other tabs in the background so switching is instant
  useEffect(() => {
    const timer = setTimeout(() => {
      (['anime-waku', 'bilibili', 'anione', 'animeruka'] as ExplorerPlatform[]).forEach((p) => {
        const key = feedCacheKey(p, 1, 'AIRING_ALL');
        if (readFeedCache(key)) return;
        fetchFeed(p, 1, 'AIRING_ALL').then((list) => {
          if (list.length > 0) writeFeedCache(key, list);
        });
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleSwitchPlatform = (p: ExplorerPlatform) => {
    setActivePlatform(p);
    setQuery('');
    setPage(1);
    setSelectedDay('ALL');
    if (p === 'anime-waku') {
      setWakuMode('AIRING_ALL');
    }
  };

  // Search results per platform+term, so retyping a term (or going back to it) is instant
  const searchCacheRef = useRef(new Map<string, WakuFeedItem[]>());

  const runSearch = async (rawTerm: string) => {
    const term = rawTerm.trim();
    if (!term) {
      setResultsFor('');
      loadRecent(activePlatform, page, wakuMode);
      return;
    }
    const requestId = ++requestIdRef.current;
    const key = `${activePlatform}:${term.toLowerCase()}`;
    const cached = searchCacheRef.current.get(key);
    if (cached) {
      setItems(cached);
      setResultsFor(term);
      setIsLoading(false);
      return;
    }
    // Keep showing the instant local matches while the full-site search runs
    setIsSearching(true);
    try {
      let found: WakuFeedItem[];
      if (activePlatform === 'anione') {
        // Current shelves first (YouTube's channel search misses some airing shows), then the
        // rest of the channel's catalogue
        const shelf = (readFeedCache(feedCacheKey('anione', 1, 'AIRING_ALL'))?.items || []).filter(
          (i) =>
            `${i.fullTitle} ${i.displayTitle} ${i.thaiTitle} ${i.romajiTitle}`
              .toLowerCase()
              .includes(term.toLowerCase())
        );
        const catalogue = await searchAniOneAnime(term);
        found = [...shelf, ...catalogue.filter((c) => !shelf.some((s) => s.url === c.url))];
      } else if (activePlatform === 'bilibili') {
        found = await searchBilibiliAnime(term);
      } else if (activePlatform === 'animeruka') {
        found = await searchRukaAnime(term);
      } else {
        found = await searchWakuAnime(term);
      }
      searchCacheRef.current.set(key, found);
      if (requestId === requestIdRef.current) {
        setItems(found);
        setResultsFor(term);
        setIsLoading(false);
      }
    } finally {
      if (requestId === requestIdRef.current) setIsSearching(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(query);
  };

  // Search the whole site as you type (debounced) instead of only filtering the airing list
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      if (resultsFor) runSearch('');
      return;
    }
    if (term.length < 2) return;
    const timer = setTimeout(() => runSearch(term), 400);
    return () => clearTimeout(timer);
  }, [query, activePlatform]);


  const platformOf = (url: string, platform?: string) => platform || detectPlatformFromUrl(url).type;

  const isAlreadyAdded = (targetUrl: string, romajiTitle: string, thaiTitle?: string) => {
    const item = items.find((i) => i.url === targetUrl);
    return existingAnimeList.some(
      (a) =>
        a.sources.some((s) => s.url === targetUrl) ||
        // Another part already saved with its own link on this platform isn't "this" one, and a
        // title match without any link on this platform isn't added yet — "+ add" attaches it
        (!(item && hasConflictingSource(a, item)) &&
          a.sources.some((s) => s.type === platformOf(targetUrl, item?.platform)) &&
          (normalizeAnimeTitle(a.title) === normalizeAnimeTitle(romajiTitle) ||
            (thaiTitle &&
              a.thaiTitle &&
              normalizeAnimeTitle(a.thaiTitle) === normalizeAnimeTitle(thaiTitle)) ||
            areAnimeMatching(a, {
              title: romajiTitle,
              thaiTitle: thaiTitle || '',
              sources: targetUrl ? [{ url: targetUrl }] : [],
            })))
    );
  };

  // Matching every item against the saved list is costly, so do it once per items/list change
  const addedUrls = useMemo(
    () =>
      new Set(
        items.filter((i) => isAlreadyAdded(i.url, i.romajiTitle, i.thaiTitle)).map((i) => i.url)
      ),
    [items, existingAnimeList]
  );

  // Filter items by shelf, audio type, day, and search query
  const shelfFilteredItems =
    // Search results span the whole channel, so the season shelf filter doesn't apply to them
    activePlatform === 'anione' && !resultsFor
      ? items.filter((item) => {
          if (aniOneShelf === 'FALL_2026') return item.vipBadge === 'Fall 2026';
          if (aniOneShelf === 'THAI_DUB') return item.vipBadge === 'พากย์ไทย';
          if (aniOneShelf === 'SUMMER_2026') return item.vipBadge === 'Summer 2026';
          return true;
        })
      : activePlatform === 'anime-waku' || activePlatform === 'animeruka'
      ? items.filter((item) => {
          if (wakuAudioFilter === 'ซับไทย') {
            return item.audioType === 'ซับไทย' || item.audioType === 'ซับ+พากย์';
          }
          if (wakuAudioFilter === 'พากย์ไทย') {
            return item.audioType === 'พากย์ไทย' || item.audioType === 'ซับ+พากย์';
          }
          return true;
        })
      : items;

  const dayFilteredItems =
    (activePlatform === 'bilibili' || activePlatform === 'anione' || activePlatform === 'animeruka') &&
    selectedDay !== 'ALL'
      ? shelfFilteredItems.filter((item) => item.broadcastDay === selectedDay)
      : shelfFilteredItems;

  const displayedItems =
    // Server search results are shown as-is (the site may match "ranma 1/2" to "Ranma ½")
    (activePlatform === 'anione' || activePlatform === 'anime-waku' || activePlatform === 'animeruka') &&
    query.trim() &&
    resultsFor !== query.trim()
      ? dayFilteredItems.filter(
          (item) =>
            item.fullTitle.toLowerCase().includes(query.trim().toLowerCase()) ||
            item.displayTitle.toLowerCase().includes(query.trim().toLowerCase()) ||
            item.thaiTitle.toLowerCase().includes(query.trim().toLowerCase()) ||
            item.romajiTitle.toLowerCase().includes(query.trim().toLowerCase())
        )
      : dayFilteredItems;

  const fall2026Items = items.filter((i) => i.vipBadge === 'Fall 2026');
  const unaddedFallItems = fall2026Items.filter(
    (i) => !addedUrls.has(i.url)
  );

  const handleAddAllFallAniOne = () => {
    for (const item of unaddedFallItems) {
      onQuickAddFromWaku(item);
    }
  };

  const unaddedWakuItems = displayedItems.filter(
    (i) => !addedUrls.has(i.url)
  );

  const handleAddAllWakuAiring = () => {
    for (const item of unaddedWakuItems) {
      onQuickAddFromWaku(item);
    }
  };

  const PLATFORM_TABS: Array<{ id: ExplorerPlatform; label: string; short: string; dot: string; source: string; href: string }> = [
    { id: 'anione', label: 'Ani-One', short: 'Ani-One', dot: 'bg-red-500', source: 'YouTube @AniOneThailand', href: 'https://www.youtube.com/@AniOneThailand' },
    { id: 'bilibili', label: 'Bilibili', short: 'Bilibili', dot: 'bg-sky-500', source: 'Bilibili Thailand', href: 'https://www.bilibili.tv/th/timeline' },
    { id: 'anime-waku', label: 'Anime-Waku', short: 'Waku', dot: 'bg-teal-400', source: 'anime-waku.com', href: 'https://anime-waku.com/anime/' },
    { id: 'animeruka', label: 'AnimeRuka', short: 'Ruka', dot: 'bg-orange-400', source: 'animeruka.com', href: 'https://animeruka.com/schedule/' },
  ];
  const activeTab = PLATFORM_TABS.find((p) => p.id === activePlatform)!;

  const chip = (active: boolean) =>
    `shrink-0 h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 border whitespace-nowrap transition-all active:scale-95 ${
      active
        ? 'bg-white text-slate-900 border-white'
        : 'bg-white/[0.03] text-slate-300 border-white/[0.07] hover:bg-white/[0.07] hover:text-white'
    }`;
  const chipCount = (active: boolean) =>
    `text-[10.5px] ${active ? 'text-slate-500' : 'text-slate-500'}`;

  const aniOneShelves: Array<{ id: AniOneShelfFilter; label: string; badge?: string }> = [
    { id: 'FALL_2026', label: '🍂 Fall 2026', badge: 'Fall 2026' },
    { id: 'THAI_DUB', label: 'พากย์ไทย', badge: 'พากย์ไทย' },
    { id: 'SUMMER_2026', label: '☀️ Summer 2026', badge: 'Summer 2026' },
    { id: 'ALL', label: 'ทั้งหมด' },
  ];

  const bulkAdd =
    activePlatform === 'anione' && aniOneShelf === 'FALL_2026' && !query.trim() && unaddedFallItems.length > 0
      ? { count: unaddedFallItems.length, run: handleAddAllFallAniOne, title: 'เพิ่ม Fall 2026 ที่ยังไม่มีทั้งหมด' }
      : activePlatform === 'anime-waku' && wakuMode === 'AIRING_ALL' && !query.trim() && unaddedWakuItems.length > 0
      ? { count: unaddedWakuItems.length, run: handleAddAllWakuAiring, title: 'เพิ่มเรื่องที่กำลังฉายที่ยังไม่มีทั้งหมด' }
      : null;

  const showDayChips =
    (activePlatform === 'bilibili' ||
      activePlatform === 'animeruka' ||
      (activePlatform === 'anione' && aniOneShelf === 'FALL_2026')) &&
    !query.trim();

  return (
    <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-5 space-y-3 sm:space-y-4 pb-8">
      {/* Platform switcher + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-2.5">
        <div className="grid grid-cols-4 p-1 rounded-2xl bg-white/[0.03] border border-white/[0.07] md:w-[34rem] shrink-0">
          {PLATFORM_TABS.map((p) => {
            const active = activePlatform === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSwitchPlatform(p.id)}
                className={`h-9 rounded-xl text-[12.5px] sm:text-[13px] font-medium flex items-center justify-center gap-1.5 sm:gap-2 transition-all ${
                  active
                    ? 'bg-white/[0.1] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
                    : 'text-slate-400 hover:text-slate-100'
                }`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${p.dot} ${active ? '' : 'opacity-50'}`} />
                <span className="sm:hidden">{p.short}</span>
                <span className="hidden sm:inline">{p.label}</span>
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSearch} className="relative flex-1 flex items-center">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`ค้นหาใน ${activeTab.label}`}
            className="w-full h-11 bg-white/[0.04] border border-white/[0.08] rounded-2xl pl-10 pr-11 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40 focus:border-fuchsia-400/50 transition-all"
          />
          {isSearching ? (
            <Loader2 className="w-4 h-4 text-fuchsia-300 animate-spin absolute right-3.5" />
          ) : (
            query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setPage(1);
                  loadRecent(activePlatform, 1);
                }}
                title="ล้างคำค้นหา"
                className="absolute right-2 w-7 h-7 rounded-full bg-white/[0.08] hover:bg-white/[0.14] text-slate-300 grid place-items-center"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )
          )}
        </form>
      </div>

      {/* Platform-specific filters */}
      {!query.trim() && (
        <div className="-mx-3 px-3 sm:mx-0 sm:px-0 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {activePlatform === 'anione' &&
            aniOneShelves.map((s) => {
              const active = aniOneShelf === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setAniOneShelf(s.id);
                    setSelectedDay('ALL');
                  }}
                  className={chip(active)}
                >
                  {s.label}
                  <span className={chipCount(active)}>
                    {s.badge ? items.filter((i) => i.vipBadge === s.badge).length : items.length}
                  </span>
                </button>
              );
            })}

          {activePlatform === 'anime-waku' && (
            <>
              <button
                type="button"
                onClick={() => {
                  setWakuMode('AIRING_ALL');
                  loadRecent('anime-waku', 1, 'AIRING_ALL');
                }}
                className={chip(wakuMode === 'AIRING_ALL')}
              >
                <Flame className="w-3.5 h-3.5 text-amber-400" />
                กำลังฉาย
                {wakuMode === 'AIRING_ALL' && <span className={chipCount(true)}>{items.length}</span>}
              </button>
              {[1, 2, 3, 4].map((pNum) => (
                <button
                  key={pNum}
                  type="button"
                  onClick={() => {
                    setWakuMode('PAGE');
                    setPage(pNum);
                    loadRecent('anime-waku', pNum, 'PAGE');
                  }}
                  className={chip(wakuMode === 'PAGE' && page === pNum)}
                >
                  หน้า {pNum}
                </button>
              ))}
              <span className="w-px h-5 bg-white/10 mx-1 shrink-0" />
              {(['ALL', 'ซับไทย', 'พากย์ไทย'] as const).map((aud) => (
                <button
                  key={aud}
                  type="button"
                  onClick={() => setWakuAudioFilter(aud)}
                  className={chip(wakuAudioFilter === aud)}
                >
                  {aud === 'ALL' ? 'ทุกเสียง' : aud}
                </button>
              ))}
            </>
          )}

          {activePlatform === 'animeruka' &&
            (['ALL', 'ซับไทย', 'พากย์ไทย'] as const).map((aud) => (
              <button
                key={aud}
                type="button"
                onClick={() => setWakuAudioFilter(aud)}
                className={chip(wakuAudioFilter === aud)}
              >
                {aud === 'ALL' ? 'ทุกเสียง' : aud}
              </button>
            ))}

          {showDayChips && (
            <>
              {(activePlatform === 'anione' || activePlatform === 'animeruka') && (
                <span className="w-px h-5 bg-white/10 mx-1 shrink-0" />
              )}
              <button type="button" onClick={() => setSelectedDay('ALL')} className={chip(selectedDay === 'ALL')}>
                ทุกวัน
                <span className={chipCount(selectedDay === 'ALL')}>{shelfFilteredItems.length}</span>
              </button>
              {BROADCAST_DAYS.filter((d) => d.id !== 'UNKNOWN').map((d) => {
                const active = selectedDay === d.id;
                return (
                  <button key={d.id} type="button" onClick={() => setSelectedDay(d.id)} className={chip(active)}>
                    {d.id === todayId && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />}
                    {d.nameTh.replace('วัน', '')}
                    <span className={chipCount(active)}>
                      {shelfFilteredItems.filter((i) => i.broadcastDay === d.id).length}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Result count + actions */}
      <div className="flex items-center justify-between gap-2 min-h-[2rem]">
        <div className="text-xs text-slate-500 min-w-0 truncate">
          {query.trim() && resultsFor ? (
            <>
              ผลการค้นหา <span className="text-white font-medium">{displayedItems.length}</span> เรื่อง
            </>
          ) : (
            <>
              <span className="text-white font-medium">{displayedItems.length}</span> เรื่อง ·{' '}
              <a
                href={activeTab.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-300 inline-flex items-center gap-1"
              >
                {activeTab.source}
                <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {bulkAdd && (
            <button
              type="button"
              onClick={bulkAdd.run}
              title={bulkAdd.title}
              className="h-8 px-3 rounded-full bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border border-fuchsia-400/25 text-fuchsia-200 text-xs font-medium flex items-center gap-1.5 active:scale-95 transition-all"
            >
              <Sparkles className="w-3.5 h-3.5" />
              เพิ่มทั้งหมด ({bulkAdd.count})
            </button>
          )}
          {activePlatform === 'anime-waku' && wakuMode === 'PAGE' && !query.trim() && (
            <button
              type="button"
              disabled={page <= 1 || isLoading}
              onClick={() => {
                const prevP = Math.max(1, page - 1);
                setPage(prevP);
                loadRecent('anime-waku', prevP, 'PAGE');
              }}
              title="หน้าก่อน"
              className="w-8 h-8 rounded-full grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] border border-white/[0.07] text-slate-300 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => loadRecent(activePlatform, page, wakuMode, true)}
            disabled={isLoading}
            title="รีเฟรชข้อมูลล่าสุด"
            className="w-8 h-8 rounded-full grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] border border-white/[0.07] text-slate-300"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          {activePlatform === 'anime-waku' && wakuMode === 'PAGE' && !query.trim() && (
            <button
              type="button"
              disabled={isLoading}
              onClick={() => {
                const nextP = page + 1;
                setPage(nextP);
                loadRecent('anime-waku', nextP, 'PAGE');
              }}
              title="หน้าถัดไป"
              className="w-8 h-8 rounded-full grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] border border-white/[0.07] text-slate-300 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Results Grid */}
      {isLoading ? (
        <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-500">
          <Loader2 className="w-7 h-7 text-fuchsia-300 animate-spin" />
          <p className="text-sm">กำลังโหลดจาก {activeTab.label}…</p>
        </div>
      ) : displayedItems.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-500 glass-panel rounded-3xl">
          ไม่พบอนิเมะในหมวดนี้
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3.5">
          {displayedItems.map((item) => {
            const added = addedUrls.has(item.url);
            const seasonCfg = SEASON_CONFIGS[item.season] || SEASON_CONFIGS.FALL;
            const dayCfg =
              item.broadcastDay && item.broadcastDay !== 'UNKNOWN'
                ? getDayConfig(item.broadcastDay)
                : null;
            const epStatus = formatAnimeEpisodeStatus(item);
            const title = item.romajiTitle || item.displayTitle;
            const subtitle = (item.thaiTitle || '').trim();
            const norm = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
            // Bilibili puts its schedule line here ("วันศุกร์ เวลา 20:00 น. • บทที่8") — already on the card
            const showSubtitle =
              subtitle !== '' &&
              !/เวลา\s*\d|บทที่\s*\d/.test(subtitle) &&
              !norm(title).includes(norm(subtitle).slice(0, 8)) &&
              !norm(subtitle).includes(norm(title).slice(0, 8));

            return (
              <div
                key={item.url}
                className="anime-card cv-auto group relative flex flex-col bg-[#0d1020] border border-white/[0.06] hover:border-white/[0.16] hover:-translate-y-0.5 rounded-2xl overflow-hidden shadow-md shadow-black/25 transition-[transform,border-color] duration-200"
              >
                <div className="relative aspect-[3/4] bg-slate-950 overflow-hidden">
                  <img
                    src={formatImageUrl(item.imageUrl)}
                    alt={item.displayTitle}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
                  />
                  <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/55 to-transparent pointer-events-none" />
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

                  {/* Top-left: schedule / season */}
                  <div className="absolute top-1.5 left-1.5 flex flex-col items-start gap-1">
                    <span
                      title={item.seasonUnknown ? 'ลงบนแพลตฟอร์มย้อนหลัง (ไม่ใช่ปีที่ฉายจริง)' : `${seasonCfg.nameTh} ${item.year}`}
                      className="px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none bg-black/70 text-white/90 ring-1 ring-white/10 whitespace-nowrap"
                    >
                      {item.seasonUnknown ? '📼 ย้อนหลัง' : `${seasonCfg.icon} ${item.year}`}
                    </span>
                    {dayCfg && (
                      <span className="px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10.5px] font-semibold leading-none bg-black/70 text-white/90 ring-1 ring-white/10 whitespace-nowrap">
                        {`${dayCfg.shortTh} ${item.broadcastTime || ''}`.trim()}
                      </span>
                    )}
                    {/พรีเมียม|vip/i.test(item.vipBadge || '') && (
                      <span className="px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10px] font-semibold leading-none bg-amber-400 text-slate-950">
                        👑 {item.vipBadge}
                      </span>
                    )}
                    {(item.audioType === 'พากย์ไทย' || item.audioType === 'ซับ+พากย์') && (
                        <span className="px-1.5 py-[3px] rounded-md text-[9px] sm:text-[10px] font-semibold leading-none bg-sky-500 text-white">
                          {item.audioType}
                        </span>
                      )}
                  </div>

                  {/* Bottom of cover: score */}
                  <div className="absolute bottom-2 inset-x-1.5 flex items-end justify-between gap-1">
                    <MalScoreBadge
                      title={item.romajiTitle || item.displayTitle}
                      year={item.seasonUnknown ? 0 : item.year}
                      compact
                      className="text-[9px] sm:text-[10.5px] !bg-black/75 !border-amber-300/30"
                    />
                  </div>
                </div>

                <div className="p-2 sm:p-2.5 flex flex-col flex-1 gap-2">
                  <div className="min-h-[2.4em]">
                    <h4
                      title={[title, subtitle].filter(Boolean).join(' — ')}
                      className={`text-[11px] sm:text-[13px] font-medium text-slate-100 leading-snug ${
                        showSubtitle ? 'line-clamp-2 sm:line-clamp-1' : 'line-clamp-2'
                      }`}
                    >
                      {title}
                    </h4>
                    {showSubtitle && (
                      <p className="hidden sm:block text-[11px] text-slate-500 line-clamp-1 mt-0.5">{subtitle}</p>
                    )}
                  </div>
                  <p className="text-[10px] sm:text-[11px] text-slate-500 truncate -mt-1" title={epStatus.badgeText}>
                    {epStatus.badgeText}
                  </p>

                  <div className="flex items-center gap-1 mt-auto">
                    <button
                      type="button"
                      onClick={() => {
                        if (added) {
                          onRemoveFromWaku?.(item);
                        } else {
                          onQuickAddFromWaku(item);
                        }
                      }}
                      title={added ? 'อยู่ในลิสต์แล้ว (แตะเพื่อนำออก)' : 'เพิ่มเข้าลิสต์'}
                      className={`flex-1 h-7 rounded-lg text-[11px] sm:text-xs font-semibold flex items-center justify-center gap-1 transition-all active:scale-95 ${
                        added
                          ? 'bg-emerald-500/10 text-emerald-300 hover:bg-rose-500/15 hover:text-rose-300'
                          : 'bg-fuchsia-500/85 hover:bg-fuchsia-400 text-white shadow-[0_2px_10px_-2px_rgba(217,70,239,0.6)]'
                      }`}
                    >
                      {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" strokeWidth={3} />}
                      <span>{added ? 'ในลิสต์' : 'เพิ่ม'}</span>
                    </button>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="เปิดหน้าเว็บต้นทาง"
                      className="w-7 h-7 rounded-lg grid place-items-center shrink-0 bg-white/[0.05] hover:bg-white/[0.1] text-slate-400 hover:text-white"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}, sameDataProps);
